import React, { useState, useEffect } from 'react';
import { Search, DollarSign, AlertTriangle, CheckCircle, Filter, Calendar, Users } from 'lucide-react';
import axios from 'axios';

const PayAttendanceFine = () => {
  const [students, setStudents] = useState([]);
  const [attendanceFineRules, setAttendanceFineRules] = useState([]);
  const [fineSettings, setFineSettings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [banks, setBanks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    course_id: '',
    semester_id: '',
    section_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ show: false, student: null });
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [selectedBank, setSelectedBank] = useState('');
  const [transactionDetails, setTransactionDetails] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchSections();
    fetchAttendanceFineRules();
    fetchFineSettings();
    fetchBanks();
  }, []);

  useEffect(() => {
    if (filters.from_date && filters.to_date) {
      fetchEligibleStudents();
    }
  }, [filters]);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchAttendanceFineRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/attendance-fines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendanceFineRules(response.data);
    } catch (error) {
      console.error('Error fetching attendance fine rules:', error);
    }
  };

  const fetchFineSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/fine-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFineSettings(response.data);
    } catch (error) {
      console.error('Error fetching fine settings:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/accounts/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const banksData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setBanks(banksData);
      // Set first bank as default
      if (banksData && banksData.length > 0) {
        setSelectedBank(banksData[0].id);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchEligibleStudents = async () => {
    setLoading(true);
    try {
      // Fetch attendance records
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);

      const attendanceResponse = await axios.get(`${API_URL}/api/attendance/report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch students
      const studentParams = new URLSearchParams();
      if (filters.course_id) studentParams.append('course_id', filters.course_id);
      if (filters.semester_id) studentParams.append('semester_id', filters.semester_id);
      if (filters.section_id) studentParams.append('section_id', filters.section_id);

      const studentsResponse = await axios.get(`${API_URL}/api/students/?${studentParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Process attendance data
      const attendanceRecords = attendanceResponse.data;
      const studentsData = studentsResponse.data;

      // Group attendance by student
      const attendanceByStudent = {};
      attendanceRecords.forEach(record => {
        if (!attendanceByStudent[record.student_id]) {
          attendanceByStudent[record.student_id] = {
            present: 0,
            absent: 0,
            total: 0
          };
        }
        if (record.status === 'Present') {
          attendanceByStudent[record.student_id].present++;
        } else {
          attendanceByStudent[record.student_id].absent++;
        }
        attendanceByStudent[record.student_id].total++;
      });

      // Get minimum percentage from attendance fine rules (use first rule or 75% as default)
      const minimumPercentage = attendanceFineRules.length > 0 
        ? attendanceFineRules[0].minimum_percentage 
        : 75;

      // Check which students have already paid fines for this date range
      const paidFinesResponse = await axios.get(
        `${API_URL}/api/attendance/paid-fines?date_from=${filters.from_date}&date_to=${filters.to_date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const paidFinesData = Array.isArray(paidFinesResponse.data) ? paidFinesResponse.data : (paidFinesResponse.data?.data || []);
      const paidStudentIds = new Set(paidFinesData.map(f => f.student_id));

      // Filter students below minimum attendance
      const eligibleStudents = studentsData
        .map(student => {
          const attendance = attendanceByStudent[student.id] || { present: 0, absent: 0, total: 0 };
          const percentage = attendance.total > 0 
            ? ((attendance.present / attendance.total) * 100).toFixed(1) 
            : 0;

          // Get fine amount
          let fineAmount = 0;
          if (attendanceFineRules.length > 0) {
            const fineRule = attendanceFineRules[0];
            const fineSetting = fineSettings.find(fs => fs.id === fineRule.fine_setting_id);
            fineAmount = fineSetting ? fineSetting.amount : 0;
          }

          // Check if fine already paid for this date range
          const hasPaid = paidStudentIds.has(student.id);

          return {
            ...student,
            attendance,
            percentage: parseFloat(percentage),
            minimumPercentage,
            fineAmount,
            isEligible: parseFloat(percentage) < minimumPercentage && attendance.total > 0,
            hasPaidFine: hasPaid
          };
        })
        .filter(student => student.isEligible);

      setStudents(eligibleStudents);
    } catch (error) {
      console.error('Error fetching eligible students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayFine = async (student) => {
    if (!paymentMode) {
      alert('Please select payment mode');
      return;
    }

    // Validate transaction details for non-cash payments
    if (paymentMode !== 'Cash' && !transactionDetails.trim()) {
      alert('Please enter transaction details for non-cash payment');
      return;
    }

    // Validate bank selection for non-cash payments
    if (paymentMode !== 'Cash' && !selectedBank) {
      alert('Please select a bank for non-cash payment');
      return;
    }

    try {
      const fineData = {
        student_id: student.id,
        student_name: student.full_name,
        admission_number: student.admission_number,
        amount: student.fineAmount,
        reason: `Attendance Fine - Low attendance (${student.percentage}% - Below ${student.minimumPercentage}% minimum)`,
        date_from: filters.from_date,
        date_to: filters.to_date,
        payment_date: new Date().toISOString().split('T')[0],
        payment_mode: paymentMode,
        bank_id: paymentMode !== 'Cash' ? selectedBank : null,
        transaction_details: paymentMode !== 'Cash' ? transactionDetails : null
      };

      const response = await axios.post(`${API_URL}/api/attendance/pay-fine`, fineData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const receiptNumber = response.data.receipt_number;
      const printReceipt = window.confirm(
        `Fine payment recorded successfully!\n\nReceipt No: ${receiptNumber}\n${response.data.bank_recorded ? 'Bank entry created\n' : ''}\n\nWould you like to print the receipt now?`
      );
      
      if (printReceipt) {
        window.open(`${API_URL}/api/attendance/fine-receipt/${receiptNumber}?token=${token}`, '_blank');
      }
      
      setPaymentModal({ show: false, student: null });
      setPaymentMode('Cash');
      setTransactionDetails('');
      // Reset to first bank
      if (banks.length > 0) {
        setSelectedBank(banks[0].id);
      }
      fetchEligibleStudents();
    } catch (error) {
      console.error('Error recording fine payment:', error);
      alert('Failed to record fine payment. Please try again.');
    }
  };

  const filteredStudents = students.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSemesters = semesters.filter(sem => 
    !filters.course_id || sem.course_id === filters.course_id
  );

  const totalFineAmount = filteredStudents.reduce((sum, student) => sum + student.fineAmount, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="bg-red-600 p-2 rounded-lg">
                  <AlertTriangle className="h-7 w-7 text-white" />
                </div>
                Pay Attendance Fine
              </h1>
              <p className="text-gray-600 mt-2">Collect fines from students with low attendance</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Eligible Students</p>
                <p className="text-4xl font-bold mt-2">{filteredStudents.length}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Users className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Total Fine Amount</p>
                <p className="text-4xl font-bold mt-2">₹{totalFineAmount}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <DollarSign className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Min. Required %</p>
                <p className="text-4xl font-bold mt-2">
                  {attendanceFineRules.length > 0 ? attendanceFineRules[0].minimum_percentage : 75}%
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <AlertTriangle className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Fine Per Student</p>
                <p className="text-4xl font-bold mt-2">
                  ₹{attendanceFineRules.length > 0 && fineSettings.length > 0 
                    ? fineSettings.find(fs => fs.id === attendanceFineRules[0].fine_setting_id)?.amount || 0
                    : 0}
                </p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <DollarSign className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-red-100 p-2 rounded-lg">
              <Filter className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                To Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Course</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                value={filters.course_id}
                onChange={(e) => setFilters({ ...filters, course_id: e.target.value, semester_id: '' })}
              >
                <option value="">All Courses</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Semester</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                value={filters.semester_id}
                onChange={(e) => setFilters({ ...filters, semester_id: e.target.value })}
                disabled={!filters.course_id}
              >
                <option value="">All Semesters</option>
                {filteredSemesters.map(semester => (
                  <option key={semester.id} value={semester.id}>{semester.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Section</label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                value={filters.section_id}
                onChange={(e) => setFilters({ ...filters, section_id: e.target.value })}
              >
                <option value="">All Sections</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search student..."
                  className="pl-10 pr-4 py-2.5 w-full border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {!filters.from_date || !filters.to_date ? (
            <div className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-700 font-semibold text-lg">Select Date Range</p>
              <p className="text-gray-500 mt-2">Please select From Date and To Date to view students with low attendance</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">S.No</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Student Name</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Admission No</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Attendance %</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Present/Total</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Fine Amount</th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr><td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-6 h-6 border-3 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-gray-600 font-medium">Loading eligible students...</span>
                      </div>
                    </td></tr>
                  ) : filteredStudents.length === 0 ? (
                    <tr><td colSpan="7" className="px-6 py-12 text-center">
                      <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                      <p className="text-gray-700 font-semibold">No Students with Low Attendance</p>
                      <p className="text-gray-500 text-sm mt-1">All students meet the minimum attendance requirement!</p>
                    </td></tr>
                  ) : (
                    filteredStudents.map((student, index) => (
                      <tr key={student.id} className="hover:bg-red-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700 font-semibold text-sm">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">{student.full_name}</div>
                          <div className="text-xs text-gray-500 mt-1">{student.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-700">
                            {student.admission_number}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-2xl font-bold text-red-600">
                              {student.percentage}%
                            </span>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-red-600"
                                style={{ width: `${student.percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">
                              Below {student.minimumPercentage}% minimum
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="text-sm font-semibold text-gray-900">
                            {student.attendance.present} / {student.attendance.total}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex px-4 py-2 rounded-lg bg-red-100 text-red-800 text-lg font-bold">
                            ₹{student.fineAmount}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {student.hasPaidFine ? (
                            <div className="flex flex-col items-center gap-2">
                              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 text-green-800 font-semibold border-2 border-green-300">
                                <CheckCircle className="h-5 w-5" />
                                Fine Paid
                              </span>
                              <span className="text-xs text-gray-500">For this period</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => setPaymentModal({ show: true, student })}
                              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-semibold shadow-lg transition-all"
                            >
                              Pay Fine
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payment Modal - Compact Design */}
        {paymentModal.show && paymentModal.student && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-red-600" />
                Pay Attendance Fine
              </h2>
              
              {/* Compact Grid Layout */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Student</p>
                  <p className="text-sm font-semibold text-gray-900">{paymentModal.student.full_name}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Admission No</p>
                  <p className="text-sm font-semibold text-gray-900">{paymentModal.student.admission_number}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Attendance</p>
                  <p className="text-lg font-bold text-red-600">{paymentModal.student.percentage}%</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500">Fine Amount</p>
                  <p className="text-lg font-bold text-red-600">₹{paymentModal.student.fineAmount}</p>
                </div>
              </div>

              {/* Payment Mode */}
              <div className="mb-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Payment Mode <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  value={paymentMode}
                  onChange={(e) => {
                    setPaymentMode(e.target.value);
                    if (e.target.value === 'Cash') {
                      setTransactionDetails('');
                    }
                  }}
                >
                  <option value="Cash">Cash</option>
                  <option value="Online">Online</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>

              {/* Bank Selection - Only for non-cash */}
              {paymentMode !== 'Cash' && (
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Bank <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                    value={selectedBank}
                    onChange={(e) => setSelectedBank(e.target.value)}
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} - {bank.account_number}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Transaction Details - Only for non-cash */}
              {paymentMode !== 'Cash' && (
                <div className="mb-4 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Transaction Details <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Transaction ID / Cheque No / Reference No"
                    className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={transactionDetails}
                    onChange={(e) => setTransactionDetails(e.target.value)}
                  />
                  <p className="text-xs text-blue-600 mt-1">
                    💡 This payment will be recorded in the selected bank
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handlePayFine(paymentModal.student)}
                  className="flex-1 bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  Pay Fine
                </button>
                <button
                  onClick={() => {
                    setPaymentModal({ show: false, student: null });
                    setPaymentMode('Cash');
                    setTransactionDetails('');
                  }}
                  className="px-6 bg-gray-200 text-gray-700 py-2.5 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayAttendanceFine;
